/**
 * Pure helpers for TaskAttachments dedupe logic (#184).
 *
 * Extracted to a non-React module so unit tests can import these without
 * pulling in apiCall / redux / MUI.
 */

/**
 * Pull lead_media_id values from UPLOAD_DOCUMENT cells in a task's checklist.
 */
export function collectLeadMediaIdsFromChecklist(checklist) {
    if (!Array.isArray(checklist)) return [];
    const ids = [];
    checklist.forEach((it) => {
        if (it?.item_type !== 'UPLOAD_DOCUMENT') return;
        const lmId = it?.response_payload?.lead_media_id;
        if (lmId !== undefined && lmId !== null && lmId !== '') ids.push(lmId);
    });
    return ids;
}

/**
 * Merge task attachments with lead-media rows uploaded via task UPLOAD_DOCUMENT
 * cells. Dedupe by file URL when present (the only namespace-safe identifier
 * shared between TaskAttachment and LeadMedia), falling back to a
 * source-prefixed id key. Task attachments win — they keep delete+download
 * affordances. Lead-media rows surviving dedup are tagged with
 * _fromTaskCell=true so the UI can badge them.
 *
 * I1 fix: TaskAttachment.id and LeadMedia.id are independent auto-increment
 * sequences, so a numeric collision (e.g. TaskAttachment.id=50 and
 * LeadMedia.id=50) does NOT mean the same file. We must never cross-check
 * across namespaces — only `media` / `file_url` is a real identity check.
 */
function buildKey(row, source) {
    if (!row) return null;
    const url = row.media || row.file_url || row.file;
    if (typeof url === 'string' && url.length) return `url:${url}`;
    if (row.id !== undefined && row.id !== null) return `${source}:${row.id}`;
    if (typeof row.original_filename === 'string' && row.original_filename.length) {
        return `${source}:name:${row.original_filename}`;
    }
    return null;
}

export function mergeAndDedupeAttachments(taskAttachments, leadMediaRows) {
    const out = [];
    const seen = new Set();
    (taskAttachments || []).forEach((a) => {
        if (!a) return;
        const key = buildKey(a, 'task');
        if (!key || seen.has(key)) return;
        seen.add(key);
        out.push(a);
    });
    (leadMediaRows || []).forEach((lm) => {
        if (!lm) return;
        const key = buildKey(lm, 'lm');
        if (!key || seen.has(key)) return;
        seen.add(key);
        out.push({ ...lm, _fromTaskCell: true });
    });
    return out;
}
